import React, { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import logEvent from "../../amplitude";
import { APP_HOME } from "../../routes/paths";
import "./ReviewLanding.css";
import ProductShowcase from "./ProductShowcase";

type IconName =
    "play" | "pause" | "comment" | "link" | "text" | "arrow" | "check" | "menu" | "close";
function Icon({ name, className = "" }: { name: IconName; className?: string }) {
    const paths: Record<IconName, React.ReactNode> = {
        play: <path d="m9 5 11 7-11 7Z" />,
        pause: <path d="M8 5v14M16 5v14" />,
        comment: <path d="M20 11a8 8 0 0 1-8 8H7l-5 3V11a9 9 0 0 1 18 0Z" />,
        link: (
            <>
                <path
                    d="m10 14 4-4M8 16l-1 1a4 4 0 0 1-6-6l5-5a4 4 0 0 1 6 0M16 8l1-1a4 4 0 0 1 6 6l-5 5a4 4 0 0 1-6 0"
                    transform="translate(1 0) scale(.9)"
                />
            </>
        ),
        text: <path d="M4 6h16M4 12h16M4 18h10" />,
        arrow: <path d="M5 12h14m-6-6 6 6-6 6" />,
        check: <path d="m5 12 4 4L19 6" />,
        menu: <path d="M4 7h16M4 12h16M4 17h16" />,
        close: <path d="m6 6 12 12M6 18 18 6" />,
    };
    return (
        <svg
            className={`qr-icon ${className}`}
            viewBox="0 0 24 24"
            fill="none"
            stroke="currentColor"
            strokeWidth="1.7"
            strokeLinecap="round"
            strokeLinejoin="round"
            aria-hidden="true"
        >
            {paths[name]}
        </svg>
    );
}

function Brand({ footer = false }: { footer?: boolean }) {
    return (
        <Link
            to="/"
            className={`qr-brand${footer ? " qr-brand-footer" : ""}`}
            aria-label="Quicksight home"
        >
            <svg viewBox="0 0 40 40" fill="none" aria-hidden="true">
                <path d="M20 3a17 17 0 0 1 17 17v16l-10-3A17 17 0 1 1 20 3Z" fill="currentColor" />
                <path d="m16 12 12 8-12 8Z" fill="white" />
            </svg>
            <span>
                Quicksight<span className="qr-brand-dot">.</span>
            </span>
        </Link>
    );
}

const faqs = [
    {
        question: "What can I do with Quicksight?",
        answer: "Upload a video, read its transcript, and collect feedback in one place. Leave comments at a specific moment or over a time range, reply to a note, and draw on a frame to make your point clear.",
    },
    {
        question: "Does everyone need an account?",
        answer: "You need an account to upload and manage videos. When you share a review link with commenting enabled, guests can leave feedback without creating an account. You choose whether a link allows viewing or commenting.",
    },
    {
        question: "How do the transcripts work?",
        answer: "Speech in your video is transcribed automatically. The words highlight as the video plays, and you can click the transcript to jump to a moment. Transcription can make mistakes, especially with background noise or overlapping voices.",
    },
    {
        question: "Can I control who sees my video?",
        answer: "You choose which videos to share and whether people with the link can view or comment. You can also set an expiration for a review link. Share links only with the people you want to invite.",
    },
    {
        question: "Is Quicksight a video editor?",
        answer: "Quicksight is a space for reviewing videos together. Make your edits in your preferred editor, then bring your video here to collect clear, timestamped feedback.",
    },
];

// Kept for the existing case-study page, which shares this call to action.
export const CTASection = ({ navigate }: { navigate: (path: string) => void }) => (
    <section className="bg-violet-50 px-6 py-20 text-center">
        <h2 className="text-3xl font-bold text-gray-800">
            Bring your next video into the conversation.
        </h2>
        <button
            className="mt-6 rounded-lg bg-primary px-6 py-3 font-semibold text-white"
            onClick={() => navigate("/signup")}
        >
            Start a review
        </button>
    </section>
);

export default function Welcome() {
    const [menuOpen, setMenuOpen] = useState(false);
    const [openFaq, setOpenFaq] = useState<number | null>(0);
    const authenticated = Boolean(localStorage.getItem("token"));
    const startPath = authenticated ? APP_HOME : "/signup";
    const loginPath = authenticated ? APP_HOME : "/login";
    useEffect(() => {
        logEvent("page_view", {
            page_title: "Quicksight",
            page_location: window.location.href,
            page_path: window.location.pathname,
        });
    }, []);
    const closeMenu = () => setMenuOpen(false);
    return (
        <div className="qr-landing">
            <a className="qr-skip" href="#main-content">
                Skip to content
            </a>
            <header className="qr-header qr-container">
                <Brand />
                <nav className="qr-desktop-nav" aria-label="Main navigation">
                    <a href="#features">Why Quicksight?</a>
                    <a href="#playground">Take a peek</a>
                    <a href="#questions">Questions</a>
                </nav>
                <div className="qr-header-actions">
                    <Link to={loginPath} className="qr-signin">
                        {authenticated ? "My videos" : "Log in"}
                    </Link>
                    <Link to={startPath} className="qr-button qr-button-small">
                        Start a review <Icon name="arrow" />
                    </Link>
                </div>
                <button
                    className="qr-menu-toggle"
                    aria-label={menuOpen ? "Close navigation" : "Open navigation"}
                    aria-expanded={menuOpen}
                    aria-controls="qr-mobile-menu"
                    onClick={() => setMenuOpen(!menuOpen)}
                >
                    <Icon name={menuOpen ? "close" : "menu"} />
                </button>
                {menuOpen && (
                    <nav
                        id="qr-mobile-menu"
                        className="qr-mobile-nav"
                        aria-label="Mobile navigation"
                        onKeyDown={event => {
                            if (event.key === "Escape") {
                                closeMenu();
                                document
                                    .querySelector<HTMLButtonElement>(".qr-menu-toggle")
                                    ?.focus();
                            }
                        }}
                    >
                        <a href="#features" onClick={closeMenu}>
                            Why Quicksight?
                        </a>
                        <a href="#playground" onClick={closeMenu}>
                            Take a peek
                        </a>
                        <a href="#questions" onClick={closeMenu}>
                            Questions
                        </a>
                        <Link to={loginPath}>{authenticated ? "My videos" : "Log in"}</Link>
                        <Link className="qr-button" to={startPath}>
                            Start a review
                        </Link>
                    </nav>
                )}
            </header>
            <main id="main-content">
                <section
                    className="qr-hero qr-hero-real qr-container"
                    aria-labelledby="qr-hero-title"
                >
                    <div className="qr-hero-copy">
                        <div className="qr-hello">
                            <span className="qr-tiny-play">
                                <Icon name="play" />
                            </span>{" "}
                            Video feedback, minus the fuss.
                        </div>
                        <h1 id="qr-hero-title">
                            A little feedback.
                            <br />A better video.
                        </h1>
                        <p>
                            Share a video. Leave a note. Follow along with the transcript.
                            Everything you need to get on the same page.
                        </p>
                        <div className="qr-hero-actions">
                            <Link to={startPath} className="qr-button">
                                Start a review <Icon name="arrow" />
                            </Link>
                            <a className="qr-demo-link" href="#playground">
                                <span>
                                    <Icon name="play" />
                                </span>
                                See the real thing
                            </a>
                        </div>
                        <div className="qr-hero-footnote">
                            <Icon name="check" /> Timestamped comments <span /> Transcripts, too
                        </div>
                    </div>
                    <ProductShowcase />
                </section>
                <section className="qr-everyone qr-container" aria-label="Who Quicksight is for">
                    <p>
                        For the things you make.
                        <br />
                        <strong>And the people you make them with.</strong>
                    </p>
                    <div>
                        <span>
                            <Icon name="play" /> The first cut
                        </span>
                        <span>
                            <Icon name="comment" /> The client notes
                        </span>
                        <span>
                            <Icon name="text" /> The class project
                        </span>
                        <span>
                            <Icon name="check" /> The final-final version
                        </span>
                    </div>
                </section>
                <section className="qr-features" id="features" aria-labelledby="qr-features-title">
                    <div className="qr-container">
                        <div className="qr-section-intro">
                            <h2 id="qr-features-title">
                                Less explaining.
                                <br />
                                More understanding.
                            </h2>
                            <p>
                                “That bit, just after the other bit”
                                <br />
                                has had a good run. Let’s be a little clearer.
                            </p>
                        </div>
                        <div className="qr-transcript-feature">
                            <div className="qr-feature-copy">
                                <span className="qr-feature-symbol">
                                    <Icon name="text" />
                                </span>
                                <h3>
                                    Every word.
                                    <br />
                                    Right there with you.
                                </h3>
                                <p>
                                    A transcript that follows your video, word for word. Read along,
                                    skim ahead, or click a line to land on the moment you’re looking
                                    for.
                                </p>
                                <a href="#playground" className="qr-text-link">
                                    Take a closer look <Icon name="arrow" />
                                </a>
                            </div>
                            <figure className="qr-real-detail qr-real-transcript">
                                <img
                                    src="/review-demo/transcript-detail.webp"
                                    width="666"
                                    height="453"
                                    alt="The actual Quicksight transcript panel, with words from the narrated M87 video"
                                    loading="lazy"
                                />
                                <figcaption>
                                    A real transcript, generated in Quicksight.
                                </figcaption>
                            </figure>
                        </div>
                        <div className="qr-feature-pair">
                            <article className="qr-moment-feature">
                                <div className="qr-feature-copy">
                                    <span className="qr-feature-symbol">
                                        <Icon name="comment" />
                                    </span>
                                    <h3>
                                        Good notes know
                                        <br />
                                        where they belong.
                                    </h3>
                                    <p>
                                        Pin feedback to the exact moment. Draw right on the frame.
                                        Keep the conversation with the video, so nothing gets lost
                                        in translation.
                                    </p>
                                </div>
                                <figure className="qr-real-detail qr-real-comments">
                                    <img
                                        src="/review-demo/comments-detail.webp"
                                        width="374"
                                        height="588"
                                        alt="Timestamped sample feedback in the actual Quicksight comments panel"
                                        loading="lazy"
                                    />
                                    <figcaption>Sample feedback. Actual review tools.</figcaption>
                                </figure>
                            </article>
                            <article className="qr-share-feature">
                                <div className="qr-feature-copy">
                                    <span className="qr-feature-symbol">
                                        <Icon name="link" />
                                    </span>
                                    <h3>
                                        Send a link.
                                        <br />
                                        Let everyone in.
                                    </h3>
                                    <p>
                                        A teammate, a client, a fresh pair of eyes. Share your video
                                        and invite feedback in their browser. Guest reviewers can
                                        join without an account.
                                    </p>
                                </div>
                                <figure className="qr-real-detail qr-real-sharing">
                                    <img
                                        src="/review-demo/share-dialog.webp"
                                        width="864"
                                        height="310"
                                        alt="Quicksight sharing settings for viewer or commenter access and link expiration"
                                        loading="lazy"
                                    />
                                    <figcaption>You choose how your video is shared.</figcaption>
                                </figure>
                            </article>
                        </div>
                    </div>
                </section>
                <section className="qr-how qr-container" aria-labelledby="qr-how-title">
                    <div>
                        <h2 id="qr-how-title">
                            A small learning curve.
                            <br />
                            As in, almost none.
                        </h2>
                        <p>
                            You’ve got a video to finish.
                            <br />
                            We’ll keep this part simple.
                        </p>
                    </div>
                    <ol>
                        <li>
                            <span className="qr-step-number">1</span>
                            <div>
                                <h3>Bring your video.</h3>
                                <p>
                                    Upload your cut. We’ll get it ready to watch and transcribe the
                                    speech.
                                </p>
                            </div>
                        </li>
                        <li>
                            <span className="qr-step-number">2</span>
                            <div>
                                <h3>Invite your people.</h3>
                                <p>
                                    Share a review link. Choose who can watch and who can leave a
                                    note.
                                </p>
                            </div>
                        </li>
                        <li>
                            <span className="qr-step-number">3</span>
                            <div>
                                <h3>Get to the good feedback.</h3>
                                <p>
                                    Read, reply, and jump between moments. Everything stays
                                    together.
                                </p>
                            </div>
                        </li>
                    </ol>
                </section>
                <section
                    className="qr-faq qr-container"
                    id="questions"
                    aria-labelledby="qr-faq-title"
                >
                    <div>
                        <span className="qr-question-doodle" aria-hidden="true">
                            ?
                        </span>
                        <h2 id="qr-faq-title">
                            A few good
                            <br />
                            questions.
                        </h2>
                        <p>
                            Something else on your mind?
                            <br />
                            <a href="mailto:abhinav@quicksight.ai">Ask us. We’re real people.</a>
                        </p>
                    </div>
                    <div className="qr-faq-list">
                        {faqs.map((faq, index) => (
                            <div className="qr-faq-item" key={faq.question}>
                                <h3>
                                    <button
                                        id={`qr-faq-button-${index}`}
                                        aria-expanded={openFaq === index}
                                        aria-controls={`qr-faq-answer-${index}`}
                                        onClick={() => setOpenFaq(openFaq === index ? null : index)}
                                    >
                                        {faq.question}
                                        <span aria-hidden="true">
                                            {openFaq === index ? "−" : "+"}
                                        </span>
                                    </button>
                                </h3>
                                <div
                                    id={`qr-faq-answer-${index}`}
                                    role="region"
                                    aria-labelledby={`qr-faq-button-${index}`}
                                    hidden={openFaq !== index}
                                >
                                    <p>{faq.answer}</p>
                                </div>
                            </div>
                        ))}
                    </div>
                </section>
                <section className="qr-closing" aria-labelledby="qr-closing-title">
                    <div className="qr-container">
                        <div className="qr-closing-bubble" aria-hidden="true">
                            <Icon name="comment" />
                            <span>“Okay, this is the one.”</span>
                        </div>
                        <h2 id="qr-closing-title">
                            Here’s to your
                            <br />
                            next great video.
                        </h2>
                        <p>And a much happier way to get there.</p>
                        <Link to={startPath} className="qr-button">
                            Start a review <Icon name="arrow" />
                        </Link>
                    </div>
                    <svg className="qr-closing-squiggle" viewBox="0 0 200 200" aria-hidden="true">
                        <path d="M5 125c34-86 75-79 73-41s-34 66-32 36 77-90 94-39-37 54-24 23 54-43 77-16" />
                    </svg>
                </section>
            </main>
            <footer className="qr-footer qr-container">
                <div>
                    <Brand footer />
                    <p>A little more clarity. A lot less back-and-forth.</p>
                </div>
                <div className="qr-footer-links">
                    <a href="#playground">See the product</a>
                    <a href="mailto:abhinav@quicksight.ai">Say hello</a>
                    <Link to={loginPath}>{authenticated ? "My videos" : "Log in"}</Link>
                </div>
                <div className="qr-footer-bottom">
                    <span>© {new Date().getFullYear()} Quicksight. Made for a better review.</span>
                    <span>Good things take a little feedback.</span>
                </div>
                <p className="qr-media-credit">
                    Screenshots use a{" "}
                    <a
                        href="https://science.nasa.gov/asset/hubble/m87-jet-knot-flaring-narration-only/"
                        target="_blank"
                        rel="noreferrer"
                    >
                        NASA/Hubble sample video
                    </a>{" "}
                    with demo feedback. Video: NASA, ESA, Z. Levay, G. Bacon, and M. Estacion
                    (STScI). Image: NASA, ESA, J. Madrid (McMaster University).
                </p>
            </footer>
        </div>
    );
}
